/**
 * AdminProChallenge — campaign control room for the 4-Week Pro Challenge.
 * Eligibility preview → invite dispatch (flag-gated) → participant management
 * → analytics/export/audit. All endpoints under /api/pro-challenge/admin/*.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trophy, CircleNotch, PaperPlaneTilt, DownloadSimple, Eye, Play } from "@phosphor-icons/react";

const Tile = ({ label, value, testid }) => (
  <div className="rounded-xl border bg-card px-4 py-3" data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-xl font-bold mt-0.5">{value}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const WEEK_DOT = { completed: "bg-green-500", current: "bg-amber-400", missed: "bg-red-500", upcoming: "bg-slate-200" };

export default function AdminProChallenge() {
  const [campaign, setCampaign] = useState(null);
  const [elig, setElig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [invites, setInvites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [busy, setBusy] = useState(null);
  const [cfg, setCfg] = useState({});

  const load = useCallback(async () => {
    const [c, e, p, i, a, au] = await Promise.all([
      api.get("/pro-challenge/admin/campaign"),
      api.get("/pro-challenge/admin/eligible"),
      api.get("/pro-challenge/admin/participants"),
      api.get("/pro-challenge/admin/invites"),
      api.get("/pro-challenge/admin/analytics"),
      api.get("/pro-challenge/admin/audit?limit=50"),
    ]);
    setCampaign(c.data);
    setCfg({
      inactivity_days: c.data.inactivity_days,
      enroll_deadline: c.data.enroll_deadline || "",
      weekly_deadline_time: c.data.weekly_deadline_time,
      reminder_hours_before: c.data.reminder_hours_before,
      reward_days: c.data.reward_days,
    });
    setElig(e.data);
    setParticipants(p.data.participants);
    setInvites(i.data.invites);
    setAnalytics(a.data);
    setAudit(au.data.audit);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Failed to load campaign data")); }, [load]);

  const rows = useMemo(() => {
    if (!elig) return [];
    return elig.rows.filter((r) => statusFilter === "all" || r.status === statusFilter)
      .filter((r) => !q || [r.truck_name, r.owner_email, r.owner_name, r.market].join(" ").toLowerCase().includes(q.toLowerCase()));
  }, [elig, q, statusFilter]);
  const eligibleRows = rows.filter((r) => r.eligible);

  const saveCfg = async (extra = {}) => {
    setBusy("cfg");
    try {
      const { data } = await api.put("/pro-challenge/admin/campaign", {
        ...cfg,
        inactivity_days: Number(cfg.inactivity_days),
        reminder_hours_before: Number(cfg.reminder_hours_before),
        reward_days: Number(cfg.reward_days),
        enroll_deadline: cfg.enroll_deadline || null,
        ...extra,
      });
      setCampaign(data);
      toast.success("Campaign settings saved");
      if ("enabled" in extra || "live_send" in extra) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(eligibleRows.map((r) => r.truck_id)));

  const exclude = async (excluded) => {
    if (!selected.size) return;
    await api.post("/pro-challenge/admin/exclude", { truck_ids: [...selected], excluded });
    setSelected(new Set());
    load();
    toast.success(excluded ? "Trucks excluded" : "Trucks re-included");
  };

  const openPreview = async () => {
    const { data } = await api.get("/pro-challenge/admin/invite-preview");
    setPreview(data);
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/pro-challenge/admin/send-test");
      data.sent ? toast.success(`Test email sent to ${data.to}`) : toast.warning(data.reason);
    } finally { setBusy(null); }
  };

  const sendInvites = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/pro-challenge/admin/send-invites", { truck_ids: [...selected], confirm: true });
      const sent = data.results.filter((r) => r.status === "sent").length;
      const queued = data.results.filter((r) => r.status === "queued_live_send_disabled").length;
      toast.success(data.live_send ? `${sent} invitation(s) sent` : `${queued} invitation(s) created (live sending OFF — nothing was emailed)`);
      setConfirmSend(false);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(null); }
  };

  const participantAction = async (id, action, extras = {}) => {
    try {
      await api.post(`/pro-challenge/admin/participants/${id}/action`, { action, ...extras });
      toast.success(`Action "${action}" applied`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const runTick = async () => {
    setBusy("tick");
    try {
      const { data } = await api.post("/pro-challenge/admin/run-tick");
      toast.success(`Tick: ${data.evaluated} evaluated, ${data.weeks_completed} weeks completed, ${data.rewards} rewards${data.skipped ? ` — ${data.skipped}` : ""}`);
      load();
    } finally { setBusy(null); }
  };

  const downloadCsv = async () => {
    const res = await api.get("/pro-challenge/admin/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "pro-challenge-results.csv";
    a.click();
  };

  if (!campaign || !elig) {
    return <div className="grid place-items-center py-32"><CircleNotch className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  const flags = campaign.flags || {};

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8" data-testid="admin-pro-challenge-page">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
        <span className="text-muted-foreground/40">/</span>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight flex items-center gap-2.5">
          <Trophy weight="duotone" className="h-7 w-7 text-primary" /> 4-Week Pro Challenge
        </h1>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={runTick} disabled={busy === "tick"} data-testid="pc-run-tick-btn">
            {busy === "tick" ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run evaluation now
          </Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={downloadCsv} data-testid="pc-export-csv-btn">
            <DownloadSimple className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {(!flags.enabled || !flags.live_send) && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm font-medium" data-testid="pc-flags-banner">
          {!flags.enabled && <span>Campaign is <strong>DISABLED</strong> — no enrollments or evaluations run. </span>}
          {!flags.live_send && <span>Live email sending is <strong>OFF</strong> — all sends are logged but nothing is delivered.</span>}
        </div>
      )}

      {analytics && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5" data-testid="pc-analytics">
          <Tile label="Eligible" value={analytics.eligible} testid="pc-tile-eligible" />
          <Tile label="Invites sent" value={`${analytics.invites_sent}${analytics.invites_queued ? ` (+${analytics.invites_queued} queued)` : ""}`} testid="pc-tile-invites" />
          <Tile label="Enrolled" value={analytics.enrollments} testid="pc-tile-enrolled" />
          <Tile label="Completion" value={`${Math.round(analytics.overall_completion_rate * 100)}%`} testid="pc-tile-completion" />
          <Tile label="Pro rewards" value={analytics.rewards_activated} testid="pc-tile-rewards" />
          <Tile label="Paid conversions" value={`${analytics.paid_conversions} ($${analytics.revenue_attributed_monthly}/mo)`} testid="pc-tile-conversions" />
        </div>
      )}
      {analytics && (
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
          {analytics.week_completion.map((w) => <span key={w.week}>W{w.week}: {w.completed} ({Math.round(w.rate * 100)}%)</span>)}
          <span>Email errors: {analytics.email_errors}</span>
          <span>Opens/clicks: {analytics.opens_clicks}</span>
        </div>
      )}

      {/* settings */}
      <div className="mt-6 rounded-2xl border bg-card p-5" data-testid="pc-settings">
        <h2 className="font-display font-bold text-lg">Campaign settings</h2>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Field label="Inactivity (days)"><input type="number" value={cfg.inactivity_days ?? ""} onChange={(e) => setCfg({ ...cfg, inactivity_days: e.target.value })} className="w-full h-9 rounded-lg border bg-background px-3 text-sm" data-testid="pc-cfg-inactivity" /></Field>
          <Field label="Enroll deadline"><input type="date" value={(cfg.enroll_deadline || "").slice(0, 10)} onChange={(e) => setCfg({ ...cfg, enroll_deadline: e.target.value })} className="w-full h-9 rounded-lg border bg-background px-3 text-sm" data-testid="pc-cfg-deadline" /></Field>
          <Field label="Weekly deadline (Sat)"><input value={cfg.weekly_deadline_time ?? ""} onChange={(e) => setCfg({ ...cfg, weekly_deadline_time: e.target.value })} className="w-full h-9 rounded-lg border bg-background px-3 text-sm" data-testid="pc-cfg-weekly-deadline" /></Field>
          <Field label="Reminder lead (hrs)"><input type="number" value={cfg.reminder_hours_before ?? ""} onChange={(e) => setCfg({ ...cfg, reminder_hours_before: e.target.value })} className="w-full h-9 rounded-lg border bg-background px-3 text-sm" data-testid="pc-cfg-reminder" /></Field>
          <Field label="Reward (days)"><input type="number" value={cfg.reward_days ?? ""} onChange={(e) => setCfg({ ...cfg, reward_days: e.target.value })} className="w-full h-9 rounded-lg border bg-background px-3 text-sm" data-testid="pc-cfg-reward" /></Field>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button size="sm" className="rounded-full" onClick={() => saveCfg()} disabled={busy === "cfg"} data-testid="pc-save-settings-btn">Save settings</Button>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={!!flags.enabled} onChange={(e) => saveCfg({ enabled: e.target.checked })} data-testid="pc-toggle-enabled" />
            Campaign enabled
          </label>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={!!flags.live_send} onChange={(e) => saveCfg({ live_send: e.target.checked })} data-testid="pc-toggle-live-send" />
            Live email sending
          </label>
          <span className="text-xs text-muted-foreground">Mid-challenge boost reward: <strong>disabled by design</strong> (feature flag)</span>
        </div>
      </div>

      {/* eligibility */}
      <div className="mt-6 rounded-2xl border bg-card p-5" data-testid="pc-eligibility">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display font-bold text-lg">Eligibility preview</h2>
          <span className="text-sm text-muted-foreground">{elig.counts.eligible} eligible of {elig.counts.total} · {elig.counts.invited} invited · {elig.counts.enrolled} enrolled · {elig.counts.excluded} excluded</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search truck, owner, email, market…" className="h-9 w-64 rounded-lg border bg-background px-3 text-sm" data-testid="pc-search-input" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-lg border bg-background px-2 text-sm" data-testid="pc-status-filter">
            {["all", "eligible", "ineligible", "invited", "enrolled", "excluded"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button variant="outline" size="sm" className="rounded-full" onClick={selectAll} data-testid="pc-select-all-btn">Select all eligible ({eligibleRows.length})</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => exclude(true)} disabled={!selected.size} data-testid="pc-exclude-btn">Exclude selected</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => exclude(false)} disabled={!selected.size} data-testid="pc-include-btn">Re-include</Button>
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={openPreview} data-testid="pc-preview-email-btn"><Eye className="h-4 w-4" /> Preview email</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={sendTest} disabled={busy === "test"} data-testid="pc-send-test-btn">Send test to me</Button>
          <Button size="sm" className="rounded-full gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setConfirmSend(true)} disabled={!selected.size} data-testid="pc-send-invites-btn">
            <PaperPlaneTilt className="h-4 w-4" /> Send invitations ({selected.size})
          </Button>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <th className="py-2 pr-2"></th><th className="py-2 pr-3">Truck</th><th className="py-2 pr-3">Owner</th><th className="py-2 pr-3">Market</th><th className="py-2 pr-3">Last posted</th><th className="py-2 pr-3">Plan</th><th className="py-2 pr-3">Status</th>
            </tr></thead>
            <tbody data-testid="pc-eligibility-table">
              {rows.slice(0, 200).map((r) => (
                <tr key={r.truck_id} className="border-b last:border-0">
                  <td className="py-2 pr-2"><input type="checkbox" disabled={!r.eligible} checked={selected.has(r.truck_id)} onChange={() => toggleSelect(r.truck_id)} data-testid={`pc-row-check-${r.truck_id}`} /></td>
                  <td className="py-2 pr-3 font-semibold">{r.truck_name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.owner_email}</td>
                  <td className="py-2 pr-3">{r.market || "—"}</td>
                  <td className="py-2 pr-3">{r.last_posted || "never"}</td>
                  <td className="py-2 pr-3">{r.plan}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.eligible ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    {!r.eligible && r.reason && <span className="ml-1.5 text-[11px] text-muted-foreground">{r.reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 200 && <p className="mt-2 text-xs text-muted-foreground">Showing first 200 of {rows.length} — refine your search.</p>}
        </div>
      </div>

      {/* participants */}
      <div className="mt-6 rounded-2xl border bg-card p-5" data-testid="pc-participants">
        <h2 className="font-display font-bold text-lg">Participants ({participants.length})</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <th className="py-2 pr-3">Truck</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Weeks</th><th className="py-2 pr-3">Reward</th><th className="py-2 pr-3">Actions</th>
            </tr></thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.id} className="border-b last:border-0 align-top" data-testid={`pc-participant-${p.truck_id}`}>
                  <td className="py-2.5 pr-3"><div className="font-semibold">{p.truck_name}</div><div className="text-xs text-muted-foreground">{p.email}</div></td>
                  <td className="py-2.5 pr-3"><span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-slate-100">{p.status}</span></td>
                  <td className="py-2.5 pr-3">
                    <div className="flex gap-1.5">
                      {p.weeks.map((w) => (
                        <span key={w.index} title={`W${w.index} ${w.start}→${w.end}: ${w.status}${w.override ? " (admin override)" : ""}`} className={`h-3.5 w-3.5 rounded-full ${WEEK_DOT[w.status] || "bg-slate-200"}`} data-testid={`pc-p-${p.truck_id}-w${w.index}`} />
                      ))}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{p.weeks[0].start} → {p.weeks[3].end}</div>
                  </td>
                  <td className="py-2.5 pr-3 text-xs">{p.reward?.activated_at ? `→ ${(p.reward.expires_at || "").slice(0, 10)}` : "—"}</td>
                  <td className="py-2.5 pr-3">
                    <select
                      className="h-8 rounded-lg border bg-background px-2 text-xs"
                      defaultValue=""
                      data-testid={`pc-action-select-${p.truck_id}`}
                      onChange={(e) => {
                        const v = e.target.value;
                        e.target.value = "";
                        if (!v) return;
                        if (v.startsWith("approve_week_")) participantAction(p.id, "approve_week", { week_index: Number(v.split("_")[2]) });
                        else if (v === "extend") participantAction(p.id, "extend", { weeks: 1 });
                        else if (v === "disqualify") { const r = window.prompt("Reason for disqualification?"); if (r !== null) participantAction(p.id, "disqualify", { reason: r }); }
                        else participantAction(p.id, v);
                      }}
                    >
                      <option value="">Action…</option>
                      {[1, 2, 3, 4].map((i) => <option key={i} value={`approve_week_${i}`}>Approve week {i}</option>)}
                      <option value="pause">Pause</option>
                      <option value="resume">Resume</option>
                      <option value="extend">Extend 1 week</option>
                      <option value="restart">Restart</option>
                      <option value="disqualify">Disqualify</option>
                      <option value="complete">Mark complete</option>
                      <option value="award_pro">Award 30-day Pro now</option>
                    </select>
                  </td>
                </tr>
              ))}
              {!participants.length && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground text-sm">No participants yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* invites + audit */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-5" data-testid="pc-invites">
          <h2 className="font-display font-bold text-lg">Invitations ({invites.length})</h2>
          <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto text-sm">
            {invites.map((i) => (
              <div key={i.id} className="flex items-center gap-2 border-b last:border-0 pb-1.5">
                <span className="font-semibold flex-1 truncate">{i.truck_name}</span>
                <span className="text-xs text-muted-foreground truncate">{i.email}</span>
                <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${i.status === "sent" || i.status === "accepted" ? "bg-green-100 text-green-800" : i.status === "not_delivered" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{i.status}</span>
              </div>
            ))}
            {!invites.length && <p className="text-muted-foreground">No invitations yet.</p>}
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-5" data-testid="pc-audit">
          <h2 className="font-display font-bold text-lg">Audit trail (latest 50)</h2>
          <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto text-xs">
            {audit.map((a) => (
              <div key={a.id} className="border-b last:border-0 pb-1.5">
                <span className="font-mono text-muted-foreground">{(a.at || "").slice(0, 16).replace("T", " ")}</span>{" "}
                <span className="font-semibold">{a.action}</span> <span className="text-muted-foreground">by {a.actor}</span>
                {a.details && Object.keys(a.details).length > 0 && <span className="text-muted-foreground"> · {JSON.stringify(a.details).slice(0, 120)}</span>}
              </div>
            ))}
            {!audit.length && <p className="text-muted-foreground">No audit entries yet.</p>}
          </div>
        </div>
      </div>

      {/* email preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setPreview(null)} data-testid="pc-preview-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div><div className="text-xs text-muted-foreground">Subject</div><div className="font-semibold text-sm">{preview.subject}</div></div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setPreview(null)}>Close</Button>
            </div>
            <iframe title="Invite preview" srcDoc={preview.html} className="flex-1 min-h-[60vh] w-full" />
          </div>
        </div>
      )}

      {/* send confirm modal */}
      {confirmSend && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="pc-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="font-display font-bold text-lg">Send {selected.size} invitation{selected.size !== 1 ? "s" : ""}?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {flags.live_send
                ? "Live sending is ON — real emails will be delivered to these owners."
                : "Live sending is OFF — invitation records will be created but NO emails will be delivered."}
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmSend(false)} data-testid="pc-confirm-cancel">Cancel</Button>
              <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={sendInvites} disabled={busy === "send"} data-testid="pc-confirm-send">
                {busy === "send" ? <CircleNotch className="h-4 w-4 animate-spin" /> : "Confirm & send"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
